import { Box, Grid, Tooltip } from '@mui/material';
import React, { FC } from 'react';
import * as yup from 'yup';

import { FormikCheckboxField } from '../../../components/FormikCheckboxField';
import { FormikDateField } from '../../../components/FormikDateField';
import { FormikDialogForm } from '../../../components/formikDialogForm/components/FormikDialogForm';
import { DialogMode } from '../../../components/formikDialogForm/types';
import { FormikDurationInput } from '../../../components/formikInputs/FormikDurationInput';
import { FormikTextField } from '../../../components/formikInputs/FormikTextField';
import { useMitarbeiterRole } from '../../auth/api/auth.hook';
import { MitarbeiterRole } from '../../auth/api/types';
import { FormikMitarbeiterAutocomplete } from '../../mitarbeiter/components/mitarbeiterAutocomplete/FormikMitarbeiterAutocomplete';
import { FormikProjektAutocomplete } from '../../projekt/components/projektAutocomplete/FormikProjektAutocomplete';
import {
  useCreateAufwandMutation,
  useCreateOwnedAufwandMutation,
  useUpdateAufwandMutation,
} from '../api/aufwandApi';
import { Aufwand, CreateAufwandDTO, UpdateAufwandDTO } from '../api/types';

export const createAufwandDTO: CreateAufwandDTO = {
  ticketId: null,
  dauerInMillis: 0,
  datum: new Date().toISOString(),
  istFakturierbar: true,
  mitarbeiterId: '',
  projektId: '',
  rechnungsnummer: null,
  taetigkeit: '',
};

export const aufwandValidationSchema = yup.object({
  ticketId: yup.string().nullable().max(24, 'Ticket ID darf nicht länger als 24 Zeichen sein.'),
  dauerInMillis: yup.number().test({
    name: 'dauerInMillis',
    test: function (value) {
      if (value && value % 900000 !== 0) {
        return this.createError({
          path: this.path,
          message: 'bitte Viertel Stunden',
        });
      }
      if (value === 0) {
        return this.createError({
          path: this.path,
          message: 'Dauer muss min. 1/4 Std. sein.',
        });
      }

      return true;
    },
  }),
  datum: yup.string().required('required'),
  mitarbeiterId: yup.string().required('required'),
  projektId: yup.string().required('required'),
  rechnungsnummer: yup.string().nullable(),
  taetigkeit: yup.string().required('required'),
});

export const AufwandDialogForm: FC<{ forExtern?: boolean }> = ({ forExtern }) => {
  const mitarbeiterRole = useMitarbeiterRole();

  const [createAufwand, { isLoading: createAufwandLoading }] = useCreateAufwandMutation();
  const [createOwnedAufwand, { isLoading: createOwnedAufwandLoading }] =
    useCreateOwnedAufwandMutation();
  const [updateAufwand, { isLoading: updateAufwandLoading }] = useUpdateAufwandMutation();

  const loading = createAufwandLoading || createOwnedAufwandLoading || updateAufwandLoading;

  const saveAufwand = (
    dto: CreateAufwandDTO | UpdateAufwandDTO,
    mode: DialogMode
  ): Promise<Aufwand> => {
    let savePromise;
    if (mode === 'create') {
      savePromise = forExtern
        ? createOwnedAufwand(dto as CreateAufwandDTO)
        : createAufwand(dto as CreateAufwandDTO);
    } else {
      savePromise = updateAufwand(dto as UpdateAufwandDTO);
    }
    return savePromise.unwrap();
  };

  return (
    <FormikDialogForm
      dialogId={'aufwand'}
      title={mode => (mode === 'create' ? 'Neuer Aufwand' : 'Aufwand bearbeiten')}
      onSubmit={saveAufwand}
      creationInitialValues={createAufwandDTO}
      validationSchema={aufwandValidationSchema}
      loading={loading}
      form={
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormikTextField
              name="taetigkeit"
              textFieldProps={{
                label: 'Tätigkeit',
                fullWidth: true,
                required: true,
                autoFocus: true,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormikDateField name={'datum'} label={'Datum'} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {/* <FormikNumberField
                            name="dauerInMillis"
                            textFieldProps={{label: "Dauer", fullWidth: true}}
                        />*/}
            <FormikDurationInput
              name="dauerInMillis"
              textFieldProps={{ label: 'Dauer in Stunden', fullWidth: true, required: true }}
            />
          </Grid>
          {!forExtern && (
            <React.Fragment>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormikProjektAutocomplete name="projektId" label="Projekt" required />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormikMitarbeiterAutocomplete
                  disabled={mitarbeiterRole !== MitarbeiterRole.ADMIN}
                  name="mitarbeiterId"
                  label="Mitarbeiter"
                />
              </Grid>
            </React.Fragment>
          )}
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Fakturierbar = Der Aufwand gehört zu einem bezahlten Kundenprojekt und wird dem Kunden in Rechnung gestellt.">
              <Box
                sx={{
                  pl: 2,
                }}
              >
                <FormikCheckboxField name={'istFakturierbar'} label={'Fakturierbar'} />
              </Box>
            </Tooltip>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormikTextField
              name="ticketId"
              textFieldProps={{ label: 'Ticket ID', fullWidth: true }}
            />
          </Grid>
        </Grid>
      }
    />
  );
};
